import React, { useEffect, useState } from 'react';
import { useThemeColors } from '@/styles';
import { shadows } from '@/styles/design-tokens';

// Floating animation wrapper for educational elements
interface FloatingElementProps {
  children: React.ReactNode;
  delay?: number;
  duration?: number;
  amplitude?: number;
}

export const FloatingElement: React.FC<FloatingElementProps> = ({
  children,
  delay = 0,
  duration = 3,
  amplitude = 10,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      style={
        {
          animation: isVisible
            ? `float ${duration}s ease-in-out infinite`
            : 'none',
          '--float-amplitude': `${amplitude}px`,
        } as React.CSSProperties
      }
    >
      {children}
    </div>
  );
};

// Fade in animation for content sections
interface FadeInProps {
  children: React.ReactNode;
  direction?: 'up' | 'down' | 'left' | 'right';
  delay?: number;
  duration?: number;
  distance?: number;
}

export const FadeIn: React.FC<FadeInProps> = ({
  children,
  direction = 'up',
  delay = 0,
  duration = 0.6,
  distance = 30,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const getTransform = () => {
    if (isVisible) return 'translate3d(0, 0, 0)';
    switch (direction) {
      case 'up':
        return `translate3d(0, ${distance}px, 0)`;
      case 'down':
        return `translate3d(0, -${distance}px, 0)`;
      case 'left':
        return `translate3d(${distance}px, 0, 0)`;
      case 'right':
        return `translate3d(-${distance}px, 0, 0)`;
      default:
        return `translate3d(0, ${distance}px, 0)`;
    }
  };

  return (
    <div
      style={{
        opacity: isVisible ? 1 : 0,
        transform: getTransform(),
        transition: `all ${duration}s cubic-bezier(0.4, 0, 0.2, 1)`,
      }}
    >
      {children}
    </div>
  );
};

// Bouncy button animation for interactive elements
interface BouncyButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit';
  style?: React.CSSProperties;
  onMouseEnter?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export const BouncyButton: React.FC<BouncyButtonProps> = ({
  children,
  onClick,
  className = '',
  disabled = false,
  type = 'button',
  style,
  onMouseEnter,
  onMouseLeave,
}) => {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`transform transition-all duration-200 ease-out hover:scale-[1.02] active:scale-[0.98] ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-soft-md'
      } ${className}`}
    >
      {children}
    </button>
  );
};

// Gentle pulse animation for call-to-action elements
interface PulsingElementProps {
  children: React.ReactNode;
  // eslint-disable-next-line no-restricted-syntax -- UI animation intensity, not a domain enum
  intensity?: 'subtle' | 'medium' | 'strong';
  speed?: 'slow' | 'normal' | 'fast';
}

export const PulsingElement: React.FC<PulsingElementProps> = ({
  children,
  intensity = 'subtle',
  speed = 'normal',
}) => {
  const getAnimationStyle = () => {
    const intensityMap = {
      subtle: '0.95, 1',
      medium: '0.9, 1',
      strong: '0.8, 1',
    };

    const speedMap = {
      slow: '3s',
      normal: '2s',
      fast: '1s',
    };

    return {
      animation: `pulse-custom ${speedMap[speed]} ease-in-out infinite`,
      '--pulse-scale': intensityMap[intensity],
    } as React.CSSProperties;
  };

  return <div style={getAnimationStyle()}>{children}</div>;
};

// Sparkle effect for success states
interface SparkleEffectProps {
  children: React.ReactNode;
  active?: boolean;
}

export const SparkleEffect: React.FC<SparkleEffectProps> = ({
  children,
  active = false,
}) => {
  const colors = useThemeColors();

  return (
    <div className="relative">
      {active && (
        <>
          <div
            className="absolute -top-1 -left-1 w-3 h-3 rounded-full animate-ping"
            style={{ backgroundColor: colors.primary, animationDelay: '0s' }}
          />
          <div
            className="absolute -top-2 -right-2 w-2 h-2 rounded-full animate-ping"
            style={{ backgroundColor: colors.primary, animationDelay: '0.5s' }}
          />
          <div
            className="absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full animate-ping"
            style={{ backgroundColor: colors.primary, animationDelay: '1s' }}
          />
        </>
      )}
      {children}
    </div>
  );
};

// Loading dots animation
interface LoadingDotsProps {
  size?: 'sm' | 'md' | 'lg';
}

export const LoadingDots: React.FC<LoadingDotsProps> = ({ size = 'md' }) => {
  const colors = useThemeColors();

  const getSizeClass = () => {
    switch (size) {
      case 'sm':
        return 'w-1.5 h-1.5';
      case 'md':
        return 'w-2 h-2';
      case 'lg':
        return 'w-3 h-3';
      default:
        return 'w-2 h-2';
    }
  };

  return (
    <div className="flex space-x-1">
      {[0, 1, 2].map(index => (
        <div
          key={index}
          className={`${getSizeClass()} rounded-full animate-bounce`}
          style={{
            backgroundColor: colors.text.secondary,
            animationDelay: `${index * 0.2}s`,
          }}
        />
      ))}
    </div>
  );
};

// Card hover animation for feature cards
interface AnimatedCardProps {
  children: React.ReactNode;
  className?: string;
  hoverEffect?: 'lift' | 'glow' | 'tilt';
}

export const AnimatedCard: React.FC<AnimatedCardProps> = ({
  children,
  className = '',
  hoverEffect = 'lift',
}) => {
  const getHoverClass = () => {
    switch (hoverEffect) {
      case 'lift':
        return 'hover:shadow-soft-lg hover:-translate-y-1';
      case 'glow':
        return 'hover:shadow-elevated';
      case 'tilt':
        return 'hover:rotate-1 hover:scale-[1.02]';
      default:
        return 'hover:shadow-soft-lg hover:-translate-y-1';
    }
  };

  return (
    <div
      className={`transition-all duration-300 ease-out transform ${getHoverClass()} ${className}`}
    >
      {children}
    </div>
  );
};

// Hover lift animation for subtle elevation
interface HoverLiftProps {
  children: React.ReactNode;
  className?: string;
}

export const HoverLift: React.FC<HoverLiftProps> = ({
  children,
  className = '',
}) => {
  return (
    <div
      className={`hover:-translate-y-1 transition-transform duration-300 ${className}`}
    >
      {children}
    </div>
  );
};

// Hover glow animation with shadow elevation
interface HoverGlowProps {
  children: React.ReactNode;
  className?: string;
}

export const HoverGlow: React.FC<HoverGlowProps> = ({
  children,
  className = '',
}) => {
  return (
    <div
      className={`hover:shadow-elevated transition-shadow duration-300 ${className}`}
      style={{
        boxShadow: shadows.soft,
      }}
    >
      {children}
    </div>
  );
};

// Smooth scale animation for interactive elements
interface SmoothScaleProps {
  children: React.ReactNode;
  className?: string;
}

export const SmoothScale: React.FC<SmoothScaleProps> = ({
  children,
  className = '',
}) => {
  return (
    <div
      className={`hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200 ${className}`}
    >
      {children}
    </div>
  );
};

// Interactive icon wrapper with subtle effects
interface InteractiveIconProps {
  children: React.ReactNode;
  className?: string;
}

export const InteractiveIcon: React.FC<InteractiveIconProps> = ({
  children,
  className = '',
}) => {
  const colors = useThemeColors();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={`hover:scale-110 transition-all duration-200 ${className}`}
      style={{
        color: isHovered ? colors.primary : 'inherit',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
    </div>
  );
};

// Progress animation for loading states
interface ProgressBarProps {
  progress: number;
  animated?: boolean;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  animated = true,
}) => {
  const colors = useThemeColors();

  return (
    <div
      className="w-full rounded-full h-2 overflow-hidden"
      style={{ backgroundColor: colors.border.light }}
    >
      <div
        className={`h-full rounded-full transition-all duration-500 ease-out ${
          animated ? 'animate-pulse' : ''
        }`}
        style={{
          width: `${Math.min(100, Math.max(0, progress))}%`,
          backgroundColor: colors.primary,
        }}
      />
    </div>
  );
};

// Success checkmark animation
interface AnimatedCheckProps {
  isVisible: boolean;
  size?: number;
}

export const AnimatedCheck: React.FC<AnimatedCheckProps> = ({
  isVisible,
  size = 24,
}) => {
  const colors = useThemeColors();

  return (
    <div
      className={`transition-all duration-500 ${
        isVisible ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
      }`}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={colors.success}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="20,6 9,17 4,12" />
      </svg>
    </div>
  );
};

// Background decoration elements
export const FloatingShapes: React.FC = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-20 left-10 w-20 h-20 bg-blue-200/20 rounded-full animate-float-slow" />
      <div className="absolute top-40 right-20 w-16 h-16 bg-green-200/20 rounded-full animate-float-medium" />
      <div className="absolute bottom-20 left-20 w-12 h-12 bg-purple-200/20 rounded-full animate-float-fast" />
      <div className="absolute bottom-40 right-10 w-24 h-24 bg-yellow-200/20 rounded-full animate-float-slow" />
    </div>
  );
};

// Form field animation wrapper
interface AnimatedFieldProps {
  children: React.ReactNode;
  isError?: boolean;
  isSuccess?: boolean;
}

export const AnimatedField: React.FC<AnimatedFieldProps> = ({
  children,
  isError = false,
  isSuccess = false,
}) => {
  return (
    <div
      className={`transition-all duration-300 ${
        isError ? 'animate-shake' : isSuccess ? 'animate-bounce-gentle' : ''
      }`}
    >
      {children}
    </div>
  );
};
